import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Utils } from '@encomply-core/services/utils/utils.service';
import { translate, TRANSLOCO_SCOPE } from '@ngneat/transloco';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { ApiService } from '../../services/api/api.service';
import { AssetService } from '../../models/assetservice.class';
import { BridgeNode } from '../../models/bridgeNode.interface';
import { CommonUtilsService } from '../../services/commonutils/common-utils.service';
import {
    getApp$,
    getBridgeNodes$,
    getSelectedPackageApp$,
    getSelectedPackageApps$,
    getSubscribedAppsMap$,
} from '../../store/selectors/apps.selector';
import { GetAppBridgeNodes, GetAppHelpDocumentation, GetAssetWithLatestVersion, GetPackageApps } from '../../store/actions/apps.actions';
import { getConnector$ } from '../../store/selectors/integrations.selector';
import { getCurrentOrganization$ } from '../../store/selectors/locations.selector';
import { GetConnector, SetConnectorId } from '../../store/actions/integrations.actions';
import { getSelectedBridgeAsset$ } from '../../store/selectors/manage-apps.selector';
import { UtilsService } from '../../services/utils/utils.service';

import { IntegrationsState } from '../../store/interface/integration-state.interface';

@Component({
    selector: 'app-fetch-assets-modal',
    templateUrl: './fetch-assets-modal.component.html',
    styleUrls: ['./fetch-assets-modal.component.scss'],
    providers: [
        {
            provide: TRANSLOCO_SCOPE,
            useValue: '',
        },
    ],
    standalone: false,
})
export class FetchAssetsModalComponent implements OnInit, OnDestroy {
    public asset: any;
    public allSubAppsArr: any[] = [];
    public currentOrgDetails: any;
    private unsubscribe = new Subject<void>();
    public appServiceId: string;
    public appAssetId: string;
    public appType: string;
    private subscribedAppsMap: { [serviceId: string]: AssetService };
    private resourceNameVsServiceIdMap: { [assetResourceName: string]: string };
    private connector: any;
    public title: string;
    public serviceIdVsConfigNameMap: { [serviceId: string]: string };
    public packagedApps: AssetService[] = [];
    public packagedApp: AssetService;

    constructor(
        private _utils: Utils,
        private _api: ApiService,
        private _libUtils: UtilsService,
        private dialog: MatDialog,
        private store$: Store
    ) {}

    private loadingBridgeApps = async asset => {
        if (asset?.bridgeNodes) {
            const bridgeNodes = asset.bridgeNodes;
            let bridgePermissions;
            if (asset.status === 'APPROVED' || this.appServiceId) {
                const userPermissions = await this._api.permissions.getBridgePermissions(asset.serviceId);
                bridgePermissions = userPermissions?.BRIDGE_ASSET?.[asset.serviceId];
                Object.keys(bridgeNodes || {}).forEach(id => {
                    const appPermissions = bridgePermissions?.[bridgeNodes[id].name];
                    if (appPermissions?.permissions) {
                        this._libUtils.addNodePermissions(bridgeNodes[id], appPermissions?.permissions);
                    } else {
                        bridgeNodes[id].noPermissions = true;
                    }
                });
            }
            this.arrangeAssetsInBridgeAsset(asset, bridgeNodes);
            this.allSubAppsArr = asset.assets;
            this.allSubAppsArr.forEach(element => {
                element['organizationId'] = asset.organizationId;
                element.assetMetaUId = element.assetMetaUId || element.id;
            });
        }
    };

    private arrangeAssetsInBridgeAsset = async (service: AssetService, bridgeNodes: Map<string, BridgeNode>) => {
        service.assets = [];
        const sortedBridges = this._utils.sortBridgeAssets(bridgeNodes);
        sortedBridges.forEach(node => {
            this.pushFilesToReports(node, sortedBridges);
            if (node.componentType !== 'FILE') {
                service.assets.push(node);
            }
        });
        return service;
    };

    private pushFilesToReports = (service: BridgeNode, nodes: BridgeNode[]) => {
        (service as any).reports = (service as any).reports || [];
        service.nextNodes.forEach(serviceNode => {
            const nextNode = nodes.find(node => node.id === serviceNode);
            if (nextNode?.componentType === 'FILE') {
                const reports = (service as any).reports;
                const existingReportIndex = reports.findIndex(report => report.id === nextNode.id);
                if (existingReportIndex !== -1) {
                    reports[existingReportIndex] = Object.assign({}, reports[existingReportIndex], nextNode);
                } else {
                    reports.push(Object.assign({}, nextNode));
                }
            }
        });
    };

    public closeModal = () => {
        this.dialog.closeAll();
    };

    public getHelpDocument = app => {
        this.store$.dispatch(
            GetAppHelpDocumentation({ serviceId: app.serviceId, bridgeServiceId: this.asset.serviceId, bridgeNodeId: app.id })
        );
    };

    public downloadHelpDocument = docObj => {
        this._utils.downloadHelpDocument(docObj);
    };

    public viewHelpDocument = (doc: any) => {
        this._api.assets.viewHelpDocument(doc);
    };

    public trackById = (_index: number, item: any) => {
        return item.id;
    };

    public getAssetDescription = asset => {
        asset.description = translate('Fetching description');
        this.store$.dispatch(
            GetAssetWithLatestVersion({
                serviceId: asset.serviceId,
            })
        );
    };

    private prepareParticipantApps = (connector: any) => {
        this.allSubAppsArr = [];
        connector?.connectorApis?.forEach(api => {
            const app = this.subscribedAppsMap?.[this.resourceNameVsServiceIdMap?.[api.assetResourceName]];
            if (!app) {
                return;
            }
            app['serviceType'] = 'ASSET';
            app['organizationId'] = this.currentOrgDetails?.id;
            this.addPermissions(app);
            app && !this.allSubAppsArr.find(allApp => allApp.serviceId === app.serviceId) && this.allSubAppsArr.push(app);
        });
    };

    private addPermissions = async app => {
        const userPermissions = await this._api.permissions.getAppPermissions(app.serviceId);
        const appPermissions = userPermissions?.ASSET?.[app.serviceId];
        this._libUtils.addNodePermissions(app, appPermissions?.permissions);
    };

    public openInboundIntegrationConfiguration = (app: AssetService) => {
        this.closeModal();
        this._libUtils.navigateToAppInboundConfiguration({
            appApi: app.restApiName,
            bridgeApi: undefined,
            reportApi: undefined,
            configName: this.serviceIdVsConfigNameMap?.[app.serviceId],
        });
    };

    public fetchBridgeAssets = (asset: AssetService) => {
        if (asset?.['serviceType'] === 'CONNECTOR' || asset?.assetType === 'CONNECTOR') {
            this.store$.dispatch(GetConnector({ resourceName: asset.restApiName }));
            this.store$.dispatch(SetConnectorId({ connectorId: asset.serviceName }));
            const dialog = this.dialog.open(FetchAssetsModalComponent, {
                panelClass: ['fetchAssetModalParent', 'subscribeAssetModal'],
                disableClose: true,
            });
            dialog.componentInstance.appType = 'CONNECTOR';
        }
    };

    prepareServiceIdVsConfigNameMap = (connector: IntegrationsState['connectors']['serviceId'][0]) => {
        this.serviceIdVsConfigNameMap = {};
        connector?.connectorApis?.forEach(api => {
            const serviceId = this.resourceNameVsServiceIdMap?.[api.assetResourceName];
            if (!serviceId) {
                return;
            }
            this.serviceIdVsConfigNameMap[serviceId] = connector.connectorName + '_' + api.apiName + '_App';
        });
    };

    /**
     * A connector's apis reference their app by resource name, while every app map in the
     * store is keyed by serviceId. Index the resource names once so the connector payload is
     * the only place a resource name is read.
     */
    private prepareResourceNameVsServiceIdMap = () => {
        this.resourceNameVsServiceIdMap = Object.values(this.subscribedAppsMap || {}).reduce((map, app) => {
            app?.restApiName && (map[app.restApiName] = app.serviceId);
            return map;
        }, {} as { [assetResourceName: string]: string });
    };

    private prepareConnectorApps = (connector: IntegrationsState['connectors']['serviceId'][0]) => {
        this.prepareServiceIdVsConfigNameMap(connector);
        this.prepareParticipantApps(connector);
    };

    private getAppPermissions = (app: AssetService) => {
        this._api.permissions.transformAppWithPermissions(app);
    };

    ngOnInit() {
        if (this.appServiceId) {
            this.store$
                .select(getApp$(this.appServiceId))
                .pipe(takeUntil(this.unsubscribe))
                .subscribe(asset => {
                    this.asset = CommonUtilsService.cloneObject(asset);
                    this.title = asset.displayName;
                });
            this.store$
                .select(getBridgeNodes$(undefined, this.appServiceId))
                .pipe(takeUntil(this.unsubscribe))
                .subscribe(nodes => {
                    this.asset.bridgeNodes = CommonUtilsService.cloneObject(nodes);
                    this.loadingBridgeApps(this.asset);
                });
            this.store$.dispatch(
                GetAppBridgeNodes({
                    serviceId: this.appServiceId,
                })
            );
        } else {
            this.store$
                .select(getSelectedBridgeAsset$)
                .pipe(
                    takeUntil(this.unsubscribe),
                    filter(count => count !== undefined && this.appType === 'BRIDGE_ASSET')
                )
                .subscribe(asset => {
                    this.asset = CommonUtilsService.cloneObject(asset);
                    this.title = this.asset.displayName || this.asset.itemName;
                    if (this.asset.bridgeNodes) {
                        this.loadingBridgeApps(this.asset);
                    } else if (this.asset.serviceId?.length > 0) {
                        this.store$.dispatch(GetAppBridgeNodes({ serviceId: this.asset.serviceId }));
                    }
                });
            this.store$
                .select(getSubscribedAppsMap$)
                .pipe(
                    takeUntil(this.unsubscribe),
                    filter(data => data !== undefined && this.appType === 'CONNECTOR')
                )
                .subscribe(apps => {
                    this.subscribedAppsMap = CommonUtilsService.cloneObject(apps);
                    this.prepareResourceNameVsServiceIdMap();
                    this.connector && this.prepareConnectorApps(this.connector);
                });
            this.store$
                .select(getSelectedPackageApp$)
                .pipe(
                    takeUntil(this.unsubscribe),
                    filter(data => data !== undefined && this.appType === 'PACKAGE')
                )
                .subscribe(app => {
                    this.store$.dispatch(GetPackageApps({ serviceId: app.serviceId }));
                    this.packagedApp = CommonUtilsService.cloneObject(app);
                    this.title = this.packagedApp?.displayName || this.packagedApp?.serviceName;
                });
            this.store$
                .select(getSelectedPackageApps$)
                .pipe(
                    takeUntil(this.unsubscribe),
                    filter(data => data !== undefined && this.appType === 'PACKAGE')
                )
                .subscribe(apps => {
                    this.allSubAppsArr = CommonUtilsService.cloneObject(apps);
                    this.allSubAppsArr.forEach(app => {
                        this.getAppPermissions(app);
                    });
                });
            this.store$
                .select(getConnector$)
                .pipe(
                    takeUntil(this.unsubscribe),
                    filter(data => data !== undefined && this.appType === 'CONNECTOR')
                )
                .subscribe(connector => {
                    this.connector = connector;
                    this.title = connector.connectorDisplayName || connector.connectorName;
                    this.prepareConnectorApps(connector);
                });
        }
        this.store$
            .select(getCurrentOrganization$)
            .pipe(takeUntil(this.unsubscribe))
            .subscribe(org => {
                this.currentOrgDetails = CommonUtilsService.cloneObject(org);
            });
    }

    ngOnDestroy(): void {
        this.unsubscribe.next();
        this.unsubscribe.complete();
    }
}
